export type TranslationValues = Record<
  string,
  string | number | boolean | null | undefined
>;

type Primitive = string | number | boolean | null | undefined;

export type DeepKeyOf<T> = T extends Primitive
  ? never
  : {
      [K in Extract<keyof T, string>]: T[K] extends Primitive
        ? K
        : T[K] extends Array<unknown>
          ? K
          : `${K}.${DeepKeyOf<T[K]>}`;
    }[Extract<keyof T, string>];

function interpolate(template: string, values?: TranslationValues) {
  if (!values) return template;
  return template.replaceAll(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (typeof value === "boolean") return value ? "true" : "false";
    return match;
  });
}

function getNestedValue(source: unknown, key: string): unknown {
  let current: unknown = source;
  for (const part of key.split(".")) {
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function createTranslator<TMessages extends Record<string, unknown>>(
  messages: TMessages,
) {
  return function t<TKey extends DeepKeyOf<TMessages>>(
    key: TKey,
    values?: TranslationValues,
  ) {
    const message = getNestedValue(messages, key as string);
    if (typeof message !== "string") return key as string;
    return interpolate(message, values);
  };
}
